import { Controller } from '@hotwired/stimulus'
import Chart from 'chart.js/auto'

// Renders a KPI sparkline as a Chart.js line chart (gradient area fill + smooth line),
// optionally with x/y axis legends.
//
// Chart.js owns the scaling here, so the Ruby helper hands over the *raw* series and the
// axis labels — no pre-scaling into an SVG box.
//
// The smooth curve uses cubicInterpolationMode 'monotone' (the Chart.js
// equivalent of d3's curveMonotoneX): rounded peaks, no overshoot. The area
// fill is a vertical canvas gradient derived from the line color, mirroring the
// original SVG gradient stops (0.40 → 0.10 → 0).
//
// Axes are opt-in (axesValue). The tiny row-2 sparks leave them off — there's no room for
// ticks at 22px tall — while the taller hero charts turn them on for x/y legends. Grid, tick
// and line colors are all read from the KPI CSS variables (kpi_charts.scss owns the dark/light
// pairing via its color-mode(dark) block), so the chart tracks the dashboard theme with no
// color logic in JS — `colorValue` is the name of the spark's `--kpi-line-*` variable.
//
// Why this is its own controller and not the shared `charts-js` one: that controller is built
// for full-size, presenter-driven analytics cards. It reads a complete Chart.js options JSON
// from an `options` <script> target and assumes a Bootstrap `.card` scaffold — toolbar +
// `chartContainer` + `canvas` + a footer `legend` target — to size itself and render an HTML
// legend, plus baggage these sparks don't want (label truncation, empty-state placeholder,
// crosshair/datalabels, the custom-renderer registry). It also themes off the `--chart-*`
// palette, not the KPI token set. These sparks are the opposite: value-driven (raw series via
// Stimulus values from a one-line helper), inline in a `<span>`, as small as 22px, themed off
// `--kpi-*`. Reusing `charts-js` would mean wrapping every spark in that card/options/legend
// payload or threading sparkline-only branches through that already-large controller — so a
// small dedicated controller is the cleaner seam. They share only "it's a Chart.js line."
export default class extends Controller {
    static values = {
        data: Array,
        labels: Array,
        color: { type: String, default: '--kpi-line-cyan' },
        fill: { type: Boolean, default: true },
        axes: { type: Boolean, default: false },
    }

    connect() {
        if (!this.dataValue || this.dataValue.length === 0) return

        const canvas = document.createElement('canvas')
        this.element.appendChild(canvas)
        this.chart = new Chart(canvas, this.#config())
    }

    disconnect() {
        this.chart?.destroy()
        this.chart = null
    }

    #config() {
        const color = this.#color()
        const labels = this.labelsValue.length
            ? this.labelsValue
            : this.dataValue.map((_, i) => i + 1)

        return {
            type: 'line',
            data: {
                labels,
                datasets: [
                    {
                        data: this.dataValue,
                        borderColor: color,
                        borderWidth: 2,
                        fill: this.fillValue,
                        backgroundColor: (ctx) => this.#areaGradient(ctx.chart, color),
                        tension: 0.4,
                        cubicInterpolationMode: 'monotone',
                        pointRadius: 0,
                        pointHoverRadius: 3,
                        pointBackgroundColor: color,
                        pointHoverBackgroundColor: color,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                animation: false,
                // Axis-less sparks bleed the line to the edges; the hero charts get a little
                // breathing room so the ticks aren't clipped.
                layout: { padding: this.axesValue ? { top: 4, right: 4 } : 0 },
                // Fire the tooltip at the nearest x position even when the cursor isn't
                // exactly on a (hidden) point — so hovering anywhere along the line works.
                interaction: { mode: 'index', intersect: false },
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        enabled: true,
                        displayColors: false,
                        callbacks: {
                            label: (item) => item.formattedValue,
                        },
                    },
                },
                scales: this.axesValue ? this.#axes() : this.#hiddenAxes(),
            },
        }
    }

    // The line/fill color. `colorValue` is the name of a `--kpi-line-*` CSS variable whose
    // theme-resolved (light/dark) value lives in kpi_charts.scss — the single source of truth.
    // Falls back to the raw value so a literal color string still works.
    #color() {
        const value = this.colorValue
        if (value.startsWith('--')) return this.#cssVar(value) || value
        return value
    }

    #axes() {
        const tick = this.#cssVar('--kpi-sub') || 'rgba(148, 163, 184, 0.7)'
        const grid = this.#cssVar('--kpi-grid') || 'rgba(148, 163, 184, 0.08)'
        const font = { size: 9, family: this.#cssVar('--kpi-mono') || 'monospace' }

        return {
            x: {
                grid: { display: false },
                border: { display: false },
                ticks: { color: tick, font, maxRotation: 0, autoSkip: true, maxTicksLimit: 6 },
            },
            y: {
                grid: { color: grid, drawTicks: false },
                border: { display: false },
                ticks: { color: tick, font, maxTicksLimit: 4, padding: 4 },
                beginAtZero: true,
            },
        }
    }

    #hiddenAxes() {
        return { x: { display: false }, y: { display: false } }
    }

    // Vertical gradient from the line color, fading to transparent — matches the original
    // SVG fill. Returns null until Chart.js has measured the chart area (first paint), at
    // which point it re-invokes this scriptable option with a valid chartArea.
    #areaGradient(chart, color) {
        const { ctx, chartArea } = chart
        if (!chartArea) return null

        const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
        gradient.addColorStop(0, this.#rgba(color, 0.4))
        gradient.addColorStop(0.55, this.#rgba(color, 0.1))
        gradient.addColorStop(1, this.#rgba(color, 0))
        return gradient
    }

    #cssVar(name) {
        return getComputedStyle(this.element).getPropertyValue(name).trim()
    }

    // Expand #rgb / #rrggbb to an rgba() string at the given alpha.
    #rgba(hex, alpha) {
        let h = hex.replace('#', '')
        if (h.length === 3) {
            h = h
                .split('')
                .map((c) => c + c)
                .join('')
        }
        const r = parseInt(h.slice(0, 2), 16)
        const g = parseInt(h.slice(2, 4), 16)
        const b = parseInt(h.slice(4, 6), 16)
        return `rgba(${r}, ${g}, ${b}, ${alpha})`
    }
}
